/**
 * ProvisionLanding — öffentliche Info-Seite „Provisionspartner werden".
 * Erreichbar ohne Login unter /provisionspartner.
 */
import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  CheckCircle2, ArrowRight, Smartphone, Megaphone, Camera, Banknote,
  Instagram, MessageCircle, CreditCard, Sparkles, TrendingUp, ShieldCheck,
} from "lucide-react";

const STEPS = [
  {
    icon: Smartphone,
    title: "App holen — kostenlos",
    text: "Registriere dich in 2 Minuten und installiere die Web-App auf deinem Handy. Keine Kreditkarte, keine Bindung, keine Verpflichtung.",
  },
  {
    icon: Megaphone,
    title: "Werbung machen",
    text: "Erzähl es in deinem Kreis: Instagram-Story, WhatsApp-Status, Freunde & Familie. Wer einen Unfall hatte, meldet sich bei dir.",
  },
  {
    icon: Camera,
    title: "Auftrag hochladen",
    text: "Kundendaten eintragen, Schadenfotos + Fahrzeugschein fotografieren, hochladen — fertig. Die App ist selbsterklärend und führt dich Schritt für Schritt durch.",
  },
  {
    icon: Banknote,
    title: "200 € kassieren",
    text: "Unser Team erstellt das Gutachten. Sobald die Versicherung zahlt, überweisen wir dir 200 € Provision direkt auf dein Konto.",
  },
];

const BENEFITS = [
  "200 € Provision pro eingereichtem Auftrag",
  "100 % kostenlos — keine Software- oder Abo-Gebühren",
  "Keine Bindung, keine Verpflichtung — du entscheidest, wie viel du machst",
  "Visitenkarten & Flyer bekommst du von uns — zum Verteilen in deinem Kreis",
  "Keine Vorkenntnisse nötig — die App führt dich durch jeden Schritt",
  "Unser Team erstellt das Gutachten — du machst nur die Fotos",
  "Auszahlung direkt auf dein Konto nach Zahlungseingang der Versicherung",
  "Live-Status in der App: du siehst jederzeit, wo dein Auftrag steht",
];

export default function ProvisionLanding() {
  return (
    <div className="min-h-screen bg-white" data-testid="provision-landing-page">
      {/* Header */}
      <header className="border-b border-zinc-200 bg-white/85 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="provision-landing-logo">
            <img src="/easy-gutachter-logo.png" alt="Easy Gutachter" className="h-12 w-auto select-none" draggable={false} />
          </Link>
          <div className="flex gap-2">
            <Link to="/login"><Button size="sm" variant="ghost">Anmelden</Button></Link>
            <Link to="/register"><Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="provision-landing-header-cta">Kostenlos starten</Button></Link>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-14 lg:py-20">
        {/* Hero */}
        <div className="text-center max-w-3xl mx-auto">
          <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
            0 € Kosten · Keine Bindung · Keine Verpflichtung
          </Badge>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mt-4">
            Fang als Provisionspartner an.<br />
            <span className="text-emerald-600">200 € pro Auftrag.</span>
          </h1>
          <p className="text-zinc-600 mt-5 text-lg leading-relaxed">
            Du willst ins Gutachter-Geschäft, aber (noch) nicht selbstständig sein?
            Kein Problem: App kostenlos holen, in deinem Kreis Werbung machen,
            Unfallschäden fotografieren und hochladen — <strong>wir erledigen den Rest</strong>.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 items-center justify-center">
            <Link to="/register">
              <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white px-8" data-testid="provision-landing-hero-cta">
                <ArrowRight className="h-4 w-4 mr-2" /> Jetzt kostenlos starten
              </Button>
            </Link>
            <a href="#so-gehts">
              <Button size="lg" variant="outline">So funktioniert's</Button>
            </a>
          </div>
          <div className="mt-6 flex items-center justify-center gap-5 text-xs text-zinc-400">
            <span className="flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> Keine Kreditkarte</span>
            <span className="flex items-center gap-1.5"><Instagram className="h-3.5 w-3.5" /> Instagram-Werbung reicht</span>
            <span className="flex items-center gap-1.5"><MessageCircle className="h-3.5 w-3.5" /> WhatsApp-Status genügt</span>
          </div>
        </div>

        {/* Schritte */}
        <div className="mt-20 sm:mt-28 scroll-mt-20" id="so-gehts">
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-center">
            In 4 Schritten zu deiner ersten Provision.
          </h2>
          <p className="text-zinc-500 text-center mt-3 max-w-xl mx-auto">
            Kein Seminar, keine Ausbildung, kein Risiko. Die App ist selbsterklärend
            und führt dich durch jeden Auftrag.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
            {STEPS.map((s, i) => (
              <div key={s.title} className="rounded-2xl border border-zinc-200 bg-white p-6 hover:border-emerald-300 hover:shadow-md transition-all" data-testid={`provision-step-${i + 1}`}>
                <div className="flex items-center justify-between">
                  <div className="h-11 w-11 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
                    <s.icon className="h-5 w-5" />
                  </div>
                  <span className="font-display text-3xl font-bold text-zinc-100">0{i + 1}</span>
                </div>
                <div className="font-display font-bold mt-4">{s.title}</div>
                <p className="text-sm text-zinc-600 mt-2 leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Werbematerial */}
        <div className="mt-20 sm:mt-28 rounded-3xl border border-zinc-200 bg-zinc-50 p-8 sm:p-12">
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="h-20 w-20 rounded-2xl bg-white border border-zinc-200 flex items-center justify-center shrink-0">
              <CreditCard className="h-9 w-9 text-emerald-600" />
            </div>
            <div className="flex-1 text-center md:text-left">
              <h3 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">
                Visitenkarten & Flyer? Bekommst du von uns.
              </h3>
              <p className="text-zinc-600 mt-3 leading-relaxed">
                Du musst nichts drucken und nichts bezahlen: Wir stellen dir
                <strong> Visitenkarten und Flyer</strong> zur Verfügung, die du in deinem
                Umfeld verteilen kannst — Werkstatt um die Ecke, Freundeskreis, Verein,
                Nachbarschaft. Dazu fertige Vorlagen für Instagram und WhatsApp.
              </p>
            </div>
          </div>
        </div>

        {/* Vorteile */}
        <div className="mt-20 sm:mt-28 max-w-3xl mx-auto">
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-center">
            Alles drin. Nichts zu verlieren.
          </h2>
          <ul className="mt-10 grid sm:grid-cols-2 gap-x-8 gap-y-4" data-testid="provision-benefits-list">
            {BENEFITS.map((b) => (
              <li key={b} className="flex items-start gap-2.5 text-sm">
                <CheckCircle2 className="h-4.5 w-4.5 text-emerald-600 mt-0.5 shrink-0" style={{ height: 18, width: 18 }} />
                <span className="text-zinc-700 leading-relaxed">{b}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Upgrade-Pfad */}
        <div className="mt-20 sm:mt-28 rounded-3xl border border-cyan-200 bg-cyan-50/40 p-8 sm:p-12 text-center" data-testid="provision-upgrade-section">
          <TrendingUp className="h-9 w-9 text-cyan-600 mx-auto" />
          <h3 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mt-4">
            Und wenn's läuft: werde selbstständig.
          </h3>
          <p className="text-zinc-600 mt-3 max-w-2xl mx-auto leading-relaxed">
            Viele starten als Provisionspartner — und steigen später um. Als selbstständiger
            Gutachter mit Easy Gutachter verdienst du <strong>ca. 750 € pro Gutachten</strong> statt
            200 € Provision. Wir helfen dir beim Umstieg, wenn du so weit bist.
          </p>
          <Link to="/preise">
            <Button variant="outline" className="mt-6 border-cyan-300" data-testid="provision-upgrade-pricing-link">
              Tarife für Selbstständige ansehen
            </Button>
          </Link>
        </div>

        {/* Final CTA */}
        <div className="mt-20 sm:mt-28 text-center pb-8">
          <Sparkles className="h-8 w-8 text-emerald-500 mx-auto" />
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-4">
            Dein erster Auftrag wartet.
          </h2>
          <p className="text-zinc-500 mt-3">
            Kostenlos registrieren, bei „Wie möchtest du arbeiten?" einfach
            <strong> „Nicht selbstständig — Aufträge einreichen"</strong> wählen — los geht's.
          </p>
          <Link to="/register">
            <Button size="lg" className="mt-7 bg-emerald-600 hover:bg-emerald-700 text-white px-10" data-testid="provision-landing-final-cta">
              <ArrowRight className="h-4 w-4 mr-2" /> Kostenlos Provisionspartner werden
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
